// DUCKS pattern
import { createSlice } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

import type { RootState } from 'store/store'
import { ClinicalReviewAccordion } from 'utils/clinicalReview/accordionPreference'
import { DropDownResponse } from 'utils/types/DropDownResponse'

import {
  Categories,
  KeywordDetail,
  Referral,
  ReferralList,
  ReferralListType,
  WaystarMatchRequest,
  ReferralFilters,
  ReferralListInputsParams,
  ReferralListTypeInputs,
  ChildReferralFilters,
  DenialResponse,
  CategoryResponse,
  PVerifyLinks,
  PVerifyMatchRequest,
  ChildReferralFiltersProps,
  ParentReferralFiltersProps,
  ClearIndividualReferralProps,
  GridStatusFiltersProps,
  GridViewAccordionProps,
  GridViewAccordionStore,
  EngagementTypes,
  CustomColumn,
} from '../types'

export interface ReferralState {
  referralDetail: Referral
  referralList: ReferralList
  selectedKeyword: KeywordDetail[]
  allKeyword: KeywordDetail[]
  isQuestionAnswersLoaded: boolean
  categoryDetailList: Categories[]
  selectedReferral: number
  selectedListTab: number
  referralListRoot: ReferralListType
  routeList: DropDownResponse[]
  refRouteList: DropDownResponse[]
  organizationList: DropDownResponse[]
  mappedPverifypayorsFullList: DropDownResponse[]
  parentFilterItems: ReferralFilters
  childFilterItems: ChildReferralFilters
  refreshForSourceMessageData: boolean
  sourceMessageTabToSet: number
  referralListInputs: ReferralListTypeInputs
  isTaskListPending: boolean
  selectedParentReferral: number
  waystarValidationChanged: boolean
  holdReasons: DropDownResponse[]
  lostReasons: DropDownResponse[]
  denialReasonList: DenialResponse[]
  referralGridViewList: CategoryResponse[]
  facilityListForParentFilter: DropDownResponse[]
  facilityListForChildFilter: DropDownResponse[]
  insuranceProvider: number
  pVerifyLinks: PVerifyLinks
  pVerifyRequestId: number
  pVerifyValidationChanged: boolean
  selectedParentTab: number
  faxStatusRefresh: boolean
  sourceHospitalList: DropDownResponse[]
  refreshRefList: boolean
  gridViewAccordionDetails: GridViewAccordionStore
  showDocument: boolean
  setGridViewZoomedListId: number | null
  selectedPatientFacId: number
  reloadPatientRefdata: boolean
  childRefdataForNav: {
    id: number
    refID: string
    status: string
    statusCode: number
    facilityName: string
  }
  showLock: boolean
  selectedAssignedUsers: { id: number; name: string; title: string }
  noteTypeFullList: EngagementTypes[]
  sourcehospListForParentFilter: DropDownResponse[]
  sourcehospListForChildFilter: DropDownResponse[]
  customColumns: CustomColumn
  lostRationaleListForParentFilter: DropDownResponse[]
  lostRationaleListForChildFilter: DropDownResponse[]
  holdRationaleListForParentFilter: DropDownResponse[]
  holdRationaleListForChildFilter: DropDownResponse[]
  parentRefOfDuplicateRefs: number
  isParentPathOrigin: boolean
  isQueuedPathOrigin: boolean
  queuedChildRefId: number
  queuedFileId: number
  isDialogOpen: boolean
  isNotesDrawerOpen: boolean
  /** Note the Notes drawer should open in edit mode, set when editing is triggered elsewhere. */
  noteIdToEdit: number | null
  isSideDrawerOpen: boolean
  capabilityViewMode: number
  capabilityExpandedAccordion: string | false
  clinicalReviewAccordionPreference: ClinicalReviewAccordion | null
  // Default saved view is applied once per login, tracked per list family
  defaultViewApplied: { parent: boolean; community: boolean }
}

const getReferralListDefaultInputData: ReferralListInputsParams = {
  search: '',
  sort: '',
  pageNumber: 1,
  pageSize: 10,
}

const initialState: ReferralState = {
  referralDetail: {},
  referralList: { data: [], totalCount: 0, hasData: true },
  selectedKeyword: [],
  allKeyword: [],
  isQuestionAnswersLoaded: false,
  categoryDetailList: [],
  selectedReferral: 0,
  selectedParentReferral: 0,
  selectedListTab: 1,
  referralListRoot: 'table',
  routeList: [],
  refRouteList: [],
  organizationList: [],
  mappedPverifypayorsFullList: [],
  parentFilterItems: {
    organizations: [],
    routes: [],
    facilities: [],
    status: [],
    sourceHospitals: [],
    createdDate: {
      from: '',
      to: '',
    },
    modifiedDate: {
      from: '',
      to: '',
    },
    followUpDate: {
      from: '',
      to: '',
    },
    admissionDate: '',
    generalFinancialClass: [],
    mappedPverifypayors: [],
    dob: [],
    noteType: [],
    typesAll: [],
    statusThreshold: false,
    selectedSavedFilter: null,
    selectedSavedFilterName: '',
  },
  childFilterItems: {
    organizations: [],
    routes: [],
    facilities: [],
    activeListStatus: [],
    allReferralListStatus: [],
    myReferralListStatus: [],
    holdReasons: [],
    lostReasons: [],
    assignedReferralListStatus: [],
    sourceHospitals: [],
    createdDate: {
      from: '',
      to: '',
    },
    modifiedDate: {
      from: '',
      to: '',
    },
    followUpDate: {
      from: '',
      to: '',
    },
    admissionDate: '',
    processingStatuses: [],
    pendingDecisionStatuses: [],
    acceptedStatuses: [],
    denialStatuses: [],
    holdReferralStatus: [],
    lostReferralStatus: [],
    types: [],
    typesAll: [],
    generalFinancialClass: [],
    mappedPverifypayors: [],
    dob: [],
    noteType: [],
    statusThreshold: false,
    selectedSavedFilter: null,
    selectedSavedFilterName: '',
  },
  refreshForSourceMessageData: false,
  sourceMessageTabToSet: 0,
  isTaskListPending: false,
  referralListInputs: {
    matched: getReferralListDefaultInputData,
    unMatched: getReferralListDefaultInputData,
    hold: getReferralListDefaultInputData,
    lost: getReferralListDefaultInputData,
    myReferral: getReferralListDefaultInputData,
    parentReferral: getReferralListDefaultInputData,
    active: getReferralListDefaultInputData,
    all: getReferralListDefaultInputData,
    fax: getReferralListDefaultInputData,
    assignedReferrals: getReferralListDefaultInputData,
    patientFax: getReferralListDefaultInputData,
  },
  waystarValidationChanged: false,
  holdReasons: [],
  lostReasons: [],
  denialReasonList: [],
  referralGridViewList: [],
  facilityListForParentFilter: [],
  facilityListForChildFilter: [],
  insuranceProvider: 0,
  pVerifyLinks: {
    primary: null,
    secondary: null,
    tertiary: null,
  },
  pVerifyRequestId: 0,
  pVerifyValidationChanged: false,
  selectedParentTab: 1,
  faxStatusRefresh: false,
  sourceHospitalList: [],
  refreshRefList: false,
  gridViewAccordionDetails: { isAccordionExpanded: true, selectedIds: [] },
  showDocument: false,
  setGridViewZoomedListId: null,
  selectedPatientFacId: 0,
  reloadPatientRefdata: false,
  childRefdataForNav: { id: 0, refID: '', status: '', statusCode: 0, facilityName: '' },
  showLock: false,
  selectedAssignedUsers: { id: 0, name: '', title: '' },
  noteTypeFullList: [],
  sourcehospListForParentFilter: [],
  sourcehospListForChildFilter: [],
  lostRationaleListForParentFilter: [],
  lostRationaleListForChildFilter: [],
  holdRationaleListForParentFilter: [],
  holdRationaleListForChildFilter: [],
  customColumns: {
    parentReferral: [],
    activeReferral: [],
    matched: [],
    unmatched: [],
    hold: [],
    lost: [],
    myReferral: [],
    all: [],
    assignedReferrals: [],
  },
  parentRefOfDuplicateRefs: 0,
  isParentPathOrigin: false,
  isQueuedPathOrigin: false,
  queuedChildRefId: 0,
  queuedFileId: 0,
  isDialogOpen: false,
  isNotesDrawerOpen: false,
  noteIdToEdit: null,
  isSideDrawerOpen: false,
  capabilityViewMode: 0,
  capabilityExpandedAccordion: false,
  clinicalReviewAccordionPreference: null,
  defaultViewApplied: { parent: false, community: false },
}
// slice
export const referralSlice = createSlice({
  name: 'referral',
  initialState,
  reducers: {
    setReferralDetail: (state, action) => {
      state.referralDetail = action.payload
    },
    setReferralMcmStatus: (state, action) => {
      if (state.referralDetail) {
        state.referralDetail.managedCareMasterStatus = action.payload
      }
    },
    setReferralList: (state, action) => {
      state.referralList = action.payload
    },
    setSelectedKeyword: (state, action) => {
      state.selectedKeyword = action.payload
    },
    setAllKeyword: (state, action) => {
      state.allKeyword = action.payload
    },
    setIsQuestionAnswersLoaded: (state, action) => {
      state.isQuestionAnswersLoaded = action.payload
    },
    setCategoryDetailList: (state, action) => {
      state.categoryDetailList = action.payload
    },
    setWaystarMatch: (state, action) => {
      const waystarInsuranceInfo = action.payload as WaystarMatchRequest

      if (
        waystarInsuranceInfo.payerType === 1 &&
        state.referralDetail.waystarInsurance &&
        state.referralDetail.insurance
      ) {
        state.referralDetail.insurance.primaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.insurance.primaryPayerName = waystarInsuranceInfo.payerName
        state.referralDetail.waystarInsurance.primaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.waystarInsurance.primaryPayerName = waystarInsuranceInfo.payerName
        state.referralDetail.insurance.primaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        state.referralDetail.waystarInsurance.primaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        if (state.referralDetail.pVerifyInsurance) {
          state.referralDetail.pVerifyInsurance.primaryPayerCode = undefined
          state.referralDetail.pVerifyInsurance.primaryPayerName = undefined
        }
      }
      if (
        waystarInsuranceInfo.payerType === 2 &&
        state.referralDetail.insurance &&
        state.referralDetail.waystarInsurance
      ) {
        state.referralDetail.insurance.secondaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.insurance.secondaryPayerName = waystarInsuranceInfo.payerName

        state.referralDetail.insurance.secondaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        state.referralDetail.waystarInsurance.secondaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.waystarInsurance.secondaryPayerName = waystarInsuranceInfo.payerName

        state.referralDetail.waystarInsurance.secondaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        if (state.referralDetail.pVerifyInsurance) {
          state.referralDetail.pVerifyInsurance.secondaryPayerCode = undefined
          state.referralDetail.pVerifyInsurance.secondaryPayerName = undefined
        }
      }
      if (
        waystarInsuranceInfo.payerType === 3 &&
        state.referralDetail.insurance &&
        state.referralDetail.waystarInsurance
      ) {
        state.referralDetail.insurance.tertiaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.insurance.tertiaryPayerName = waystarInsuranceInfo.payerName

        state.referralDetail.insurance.tertiaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        state.referralDetail.waystarInsurance.tertiaryPayerCode = waystarInsuranceInfo.payerCode
        state.referralDetail.waystarInsurance.tertiaryPayerName = waystarInsuranceInfo.payerName

        state.referralDetail.waystarInsurance.tertiaryWaystarInfo =
          waystarInsuranceInfo?.waystarValidateInfo
        if (state.referralDetail.pVerifyInsurance) {
          state.referralDetail.pVerifyInsurance.tertiaryPayerCode = undefined
          state.referralDetail.pVerifyInsurance.tertiaryPayerName = undefined
        }
      }
    },
    setPVerifyMatch: (state, action) => {
      const pVerifyMatchInfo = action.payload as PVerifyMatchRequest

      if (
        pVerifyMatchInfo.payerType === 1 &&
        state.referralDetail.insurance &&
        state.referralDetail.pVerifyInsurance
      ) {
        state.referralDetail.insurance.primaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.insurance.primaryPayerName = pVerifyMatchInfo.payerName
        state.referralDetail.pVerifyInsurance.primaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.pVerifyInsurance.primaryPayerName = pVerifyMatchInfo.payerName
        if (state.referralDetail.waystarInsurance) {
          state.referralDetail.waystarInsurance.primaryPayerCode = undefined
          state.referralDetail.waystarInsurance.primaryPayerName = undefined
          state.referralDetail.waystarInsurance.primaryWaystarInfo = undefined
        }
      }
      if (
        pVerifyMatchInfo.payerType === 2 &&
        state.referralDetail.insurance &&
        state.referralDetail.pVerifyInsurance
      ) {
        state.referralDetail.insurance.secondaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.insurance.secondaryPayerName = pVerifyMatchInfo.payerName
        state.referralDetail.pVerifyInsurance.secondaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.pVerifyInsurance.secondaryPayerName = pVerifyMatchInfo.payerName
        if (state.referralDetail.waystarInsurance) {
          state.referralDetail.waystarInsurance.secondaryPayerCode = undefined
          state.referralDetail.waystarInsurance.secondaryPayerName = undefined
          state.referralDetail.waystarInsurance.secondaryWaystarInfo = undefined
        }
      }
      if (
        pVerifyMatchInfo.payerType === 3 &&
        state.referralDetail.insurance &&
        state.referralDetail.pVerifyInsurance
      ) {
        state.referralDetail.insurance.tertiaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.insurance.tertiaryPayerName = pVerifyMatchInfo.payerName
        state.referralDetail.pVerifyInsurance.tertiaryPayerCode = pVerifyMatchInfo.payerCode
        state.referralDetail.pVerifyInsurance.tertiaryPayerName = pVerifyMatchInfo.payerName
        if (state.referralDetail.waystarInsurance) {
          state.referralDetail.waystarInsurance.tertiaryPayerCode = undefined
          state.referralDetail.waystarInsurance.tertiaryPayerName = undefined
          state.referralDetail.waystarInsurance.tertiaryWaystarInfo = undefined
        }
      }
    },
    setSelectedReferral: (state, action) => {
      state.selectedReferral = action.payload
    },
    setSelectedParentReferral: (state, action) => {
      state.selectedParentReferral = action.payload
    },

    setSelectedListTab: (state, action) => {
      state.selectedListTab = action.payload
    },
    setReferralListRoot: (state, action) => {
      state.referralListRoot = action.payload
    },
    setRouteList: (state, action) => {
      state.routeList = action.payload
    },
    setRefRouteList: (state, action) => {
      state.refRouteList = action.payload
    },
    setSourceHospitalList: (state, action) => {
      state.sourceHospitalList = action.payload
    },

    setOrganizationList: (state, action) => {
      state.organizationList = action.payload
    },

    setMappedPverifyPayorsList: (state, action) => {
      state.mappedPverifypayorsFullList = action.payload
    },
    setParentFilterItems: (state, action) => {
      const data = action.payload as ParentReferralFiltersProps | undefined
      if (!data) {
        state.parentFilterItems = { ...initialState.parentFilterItems }
        return
      }
      switch (data.type) {
        case 'parentReferral':
          state.parentFilterItems.organizations = data.organizations
          state.parentFilterItems.facilities = data.facilities
          state.parentFilterItems.routes = data.routes
          state.parentFilterItems.status = data.status
          state.parentFilterItems.sourceHospitals = data.sourceHospitals
          state.parentFilterItems.createdDate = data.createdDate
          state.parentFilterItems.modifiedDate = data.modifiedDate
          state.parentFilterItems.followUpDate = data.followUpDate
          state.parentFilterItems.admissionDate = data.admissionDate
          state.parentFilterItems.generalFinancialClass = data.generalFinancialClass
          state.parentFilterItems.noteType = data.noteType
          state.parentFilterItems.statusThreshold = data.statusThreshold ?? false
          state.parentFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.parentFilterItems.dob = data.dob
          state.parentFilterItems.typesAll = data.typesAll ?? []
          state.parentFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.parentFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''
          break
        case 'patientFax':
          state.parentFilterItems.organizations = data.organizations
          state.parentFilterItems.facilities = data.facilities
          state.parentFilterItems.sourceHospitals = data.sourceHospitals
          state.parentFilterItems.createdDate = data.createdDate
          state.parentFilterItems.modifiedDate = data.modifiedDate
          state.parentFilterItems.followUpDate = data.followUpDate
          state.parentFilterItems.admissionDate = data.admissionDate
          state.parentFilterItems.routes = data.routes
          state.parentFilterItems.generalFinancialClass = data.generalFinancialClass
          state.parentFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.parentFilterItems.dob = data.dob
          state.parentFilterItems.noteType = data.noteType
          state.parentFilterItems.statusThreshold = data.statusThreshold ?? false
          state.parentFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.parentFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        default:
          break
      }
    },
    setChildFilterItems: (state, action) => {
      const data = action.payload as ChildReferralFiltersProps | undefined
      if (!data) {
        state.childFilterItems = { ...initialState.childFilterItems }
        return
      }
      switch (data.type) {
        case 'myReferral':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.myReferralListStatus = data.statusList ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.typesAll = data.typesAll ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'activeReferral':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.activeListStatus = data.statusList ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.types = data.types ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'assignedReferrals':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.assignedReferralListStatus = data.statusList ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'hold':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.holdReasons = data.holdReasons ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.holdReferralStatus = data.statusList ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'lost':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.lostReasons = data.lostReasons ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.lostReferralStatus = data.statusList ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'unmatched':
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'fax':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'all':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.allReferralListStatus = data.statusList ?? []
          state.childFilterItems.typesAll = data.typesAll ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        case 'gridview':
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.types = data.types ?? []
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
        default:
          state.childFilterItems.organizations = data.organizations ?? []
          state.childFilterItems.facilities = data.facilities ?? []
          state.childFilterItems.routes = data.routes ?? []
          state.childFilterItems.sourceHospitals = data.sourceHospitals
          state.childFilterItems.createdDate = data.createdDate
          state.childFilterItems.modifiedDate = data.modifiedDate
          state.childFilterItems.noteType = data.noteType
          state.childFilterItems.statusThreshold = data.statusThreshold ?? false
          state.childFilterItems.followUpDate = data.followUpDate
          state.childFilterItems.admissionDate = data.admissionDate
          state.childFilterItems.generalFinancialClass = data.generalFinancialClass
          state.childFilterItems.mappedPverifypayors = data.mappedPverifypayors
          state.childFilterItems.dob = data.dob ?? []
          state.childFilterItems.selectedSavedFilter = data.selectedSavedFilter ?? null
          state.childFilterItems.selectedSavedFilterName = data.selectedSavedFilterName ?? ''

          break
      }
    },

    setGridStatusFilterItems: (state, action) => {
      const data = action.payload as GridStatusFiltersProps
      switch (data.type) {
        case 1:
          state.childFilterItems.processingStatuses = data.statuses ?? []
          break
        case 2:
          state.childFilterItems.pendingDecisionStatuses = data.statuses ?? []
          break
        case 3:
          state.childFilterItems.acceptedStatuses = data.statuses ?? []
          break
        case 4:
          state.childFilterItems.denialStatuses = data.statuses ?? []
          break
        default:
          break
      }
    },
    clearIndividualParentFilter: (state, action) => {
      const data = action.payload as ClearIndividualReferralProps
      switch (data.filterId) {
        case 1:
          state.parentFilterItems.organizations = []
          state.parentFilterItems.facilities = []
          break
        case 2:
          state.parentFilterItems.facilities = []
          break
        case 3:
          state.parentFilterItems.sourceHospitals = []
          break
        case 4:
          state.parentFilterItems.admissionDate = ''
          break
        case 5:
          state.parentFilterItems.status = []
          break
        case 6:
          state.parentFilterItems.routes = []
          break
        case 8:
          state.parentFilterItems.createdDate = {
            from: '',
            to: '',
          }
          break
        case 11:
          state.parentFilterItems.typesAll = []
          break
        case 12:
          state.parentFilterItems.generalFinancialClass = []
          break
        case 13:
          state.parentFilterItems.mappedPverifypayors = []
          break
        case 14:
          state.parentFilterItems.dob = []
          break
        case 15:
          state.parentFilterItems.modifiedDate = {
            from: '',
            to: '',
          }
          break
        case 16:
          state.parentFilterItems.noteType = []
          break
        case 17:
          state.parentFilterItems.statusThreshold = false
          break
        case 18:
          state.parentFilterItems.followUpDate = {
            from: '',
            to: '',
          }
          break
        default:
          break
      }
    },
    clearIndividualChildFilter: (state, action) => {
      const data = action.payload as ClearIndividualReferralProps
      switch (data.filterId) {
        case 1:
          state.childFilterItems.organizations = []
          state.childFilterItems.facilities = []
          break
        case 2:
          state.childFilterItems.facilities = []
          break
        case 3:
          state.childFilterItems.sourceHospitals = []
          break
        case 4:
          state.childFilterItems.admissionDate = ''
          break
        case 5:
          switch (data.type) {
            case 'all':
              state.childFilterItems.allReferralListStatus = []
              break
            case 'myReferral':
              state.childFilterItems.myReferralListStatus = []
              break
            case 'activeReferral':
              state.childFilterItems.activeListStatus = []
              break
            case 'assignedReferrals':
              state.childFilterItems.assignedReferralListStatus = []
              break
            case 'hold':
              state.childFilterItems.holdReferralStatus = []
              break
            case 'lost':
              state.childFilterItems.lostReferralStatus = []
              break
            default:
              break
          }
          break
        case 6:
          state.childFilterItems.routes = []
          break
        case 7:
          state.childFilterItems.holdReasons = []
          break
        case 8:
          state.childFilterItems.createdDate = {
            from: '',
            to: '',
          }
          break
        case 9:
          state.childFilterItems.lostReasons = []
          break
        case 10:
          state.childFilterItems.types = []
          break
        case 11:
          state.childFilterItems.typesAll = []
          break
        case 12:
          state.childFilterItems.generalFinancialClass = []
          break
        case 13:
          state.childFilterItems.mappedPverifypayors = []
          break
        case 14:
          state.childFilterItems.dob = []
          break
        case 15:
          state.childFilterItems.modifiedDate = {
            from: '',
            to: '',
          }
          break
        case 16:
          state.childFilterItems.noteType = []
          break
        case 17:
          state.childFilterItems.statusThreshold = false
          break
        case 18:
          state.childFilterItems.followUpDate = {
            from: '',
            to: '',
          }
          break
        default:
          break
      }
    },
    setRefreshForSourceMessageData: (state, action) => {
      state.refreshForSourceMessageData = action.payload
    },
    setSourceMessageTabToSet: (state, action) => {
      state.sourceMessageTabToSet = action.payload
    },
    setReferralListInputs: (state, action) => {
      const props = action.payload as { type: string; data: ReferralListInputsParams }
      switch (props.type) {
        case 'lost':
          state.referralListInputs.lost = props.data
          break
        case 'hold':
          state.referralListInputs.hold = props.data
          break
        case 'unmatched':
          state.referralListInputs.unMatched = props.data
          break
        case 'myReferral':
          state.referralListInputs.myReferral = props.data
          break
        case 'parentReferral':
          state.referralListInputs.parentReferral = props.data
          break
        case 'activeReferral':
          state.referralListInputs.active = props.data
          break
        case 'all':
          state.referralListInputs.all = props.data
          break
        case 'fax':
          state.referralListInputs.fax = props.data
          break
        case 'assignedReferrals':
          state.referralListInputs.assignedReferrals = props.data
          break
        case 'patientFax':
          state.referralListInputs.patientFax = props.data
          break
        default:
          break
      }
    },
    setIsTaskListPending: (state, action) => {
      state.isTaskListPending = action.payload
    },
    setWaystarValidationChanged: state => {
      state.waystarValidationChanged = !state.waystarValidationChanged
    },
    setHoldReasons: (state, action) => {
      state.holdReasons = action.payload
    },
    setLostReasons: (state, action) => {
      state.lostReasons = action.payload
    },
    setDenialReasonList: (state, action) => {
      state.denialReasonList = action.payload
    },
    setReferralGridViewList: (state, action) => {
      state.referralGridViewList = action.payload
    },
    setFacilityListForParentFilter: (state, action) => {
      state.facilityListForParentFilter = action.payload
    },
    setFacilityListForChildFilter: (state, action) => {
      state.facilityListForChildFilter = action.payload
    },
    setSourcehospListForParentFilter: (state, action) => {
      state.sourcehospListForParentFilter = action.payload
    },
    setSourcehospListForChildFilter: (state, action) => {
      state.sourcehospListForChildFilter = action.payload
    },
    setHoldRationaleListForParentFilter: (state, action) => {
      state.holdRationaleListForParentFilter = action.payload
    },
    setHoldRationaleListForChildFilter: (state, action) => {
      state.holdRationaleListForChildFilter = action.payload
    },
    setLostRationaleListForParentFilter: (state, action) => {
      state.lostRationaleListForParentFilter = action.payload
    },
    setLostRationaleListForChildFilter: (state, action) => {
      state.lostRationaleListForChildFilter = action.payload
    },
    setInsuranceProvider: (state, action) => {
      state.insuranceProvider = action.payload
    },
    setPVerifyLinks: (state, action) => {
      state.pVerifyLinks = action.payload
    },
    setPVerifyRequestId: (state, action) => {
      state.pVerifyRequestId = action.payload
    },
    setPVerifyValidationChanged: state => {
      state.pVerifyValidationChanged = !state.pVerifyValidationChanged
    },
    setSelectedParentTab: (state, action) => {
      state.selectedParentTab = action.payload
    },
    setReferralComparisonId: (state, action) => {
      state.referralDetail.referralComparisonId = action.payload
    },
    setFaxStatusRefresh: state => {
      state.faxStatusRefresh = !state.faxStatusRefresh
    },
    setOcrCompletionStatus: (state, action) => {
      state.referralDetail.ocrGenerated = action.payload
    },
    setAIProcessingStatus: (state, action) => {
      state.referralDetail.isAIAnswerGenerated = action.payload
    },
    setRefreshRefList: state => {
      state.refreshRefList = !state.refreshRefList
    },
    setGridViewAccordionDetails: (state, action) => {
      const data = action.payload as GridViewAccordionProps
      if (data.isAll) {
        state.gridViewAccordionDetails = {
          isAccordionExpanded: !state.gridViewAccordionDetails.isAccordionExpanded,
          selectedIds: [],
        }
      } else {
        const referralId = data.referralId ?? 0
        const { selectedIds } = state.gridViewAccordionDetails

        // Check if the ID already exists in the array
        if (selectedIds.includes(referralId)) {
          // Remove the ID from the array
          state.gridViewAccordionDetails.selectedIds = selectedIds.filter(id => id !== referralId)
        } else {
          // Add the ID to the array
          state.gridViewAccordionDetails.selectedIds = [...selectedIds, referralId]
        }
      }
    },
    setShowDocument: state => {
      state.showDocument = !state.showDocument
    },
    setGridViewZoomedListId: (state, action) => {
      state.setGridViewZoomedListId = action.payload
    },

    setSelectedPatientfacilityId: (state, action) => {
      state.selectedPatientFacId = action.payload
    },
    setReloadPatientRefdata: state => {
      state.reloadPatientRefdata = !state.reloadPatientRefdata
    },
    setChildRefdataForNav: (state, action) => {
      state.childRefdataForNav = action.payload
    },
    setShowLock: (state, action) => {
      state.showLock = action.payload
    },
    setSelectedAssignedUsers: (state, action) => {
      state.selectedAssignedUsers = action.payload
    },
    setNoteTypeFullList: (state, action) => {
      state.noteTypeFullList = action.payload
    },
    setCustomColumns: (state, action) => {
      const { listType, columns } = action.payload
      switch (listType) {
        case 'parentReferral':
          state.customColumns.parentReferral = columns
          break
        case 'activeReferral':
          state.customColumns.activeReferral = columns
          break
        case 'matched':
          state.customColumns.matched = columns
          break
        case 'unmatched':
          state.customColumns.unmatched = columns
          break
        case 'hold':
          state.customColumns.hold = columns
          break
        case 'lost':
          state.customColumns.lost = columns
          break
        case 'myReferral':
          state.customColumns.myReferral = columns
          break
        case 'all':
          state.customColumns.all = columns
          break
        case 'assignedReferrals':
          state.customColumns.assignedReferrals = columns
          break
        default:
          break
      }
    },
    setParentRefOfDuplicateRefs: (state, action) => {
      state.parentRefOfDuplicateRefs = action.payload
    },
    setIsParentPathOrigin: (state, action) => {
      state.isParentPathOrigin = action.payload
    },
    setIsQueuedPathOrigin: (state, action) => {
      state.isQueuedPathOrigin = action.payload
    },
    setQueuedchildRefId: (state, action) => {
      state.queuedChildRefId = action.payload
    },
    setQueuedFileId: (state, action) => {
      state.queuedFileId = action.payload
    },
    setIsDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload
    },
    setIsNotesDrawerOpen: (state, action) => {
      state.isNotesDrawerOpen = action.payload
    },
    setNoteIdToEdit: (state, action) => {
      state.noteIdToEdit = action.payload
    },
    setIsSideDrawerOpen: (state, action) => {
      state.isSideDrawerOpen = action.payload
    },
    updateReferralAttachment: (state, action) => {
      state.referralDetail.attachment = action.payload
    },
    setCapabilityViewMode: (state, action) => {
      state.capabilityViewMode = action.payload
    },
    setCapabilityExpandedAccordion: (state, action) => {
      state.capabilityExpandedAccordion = action.payload
    },
    setClinicalReviewAccordionPreference: (state, action) => {
      state.clinicalReviewAccordionPreference = action.payload
    },
    setDefaultViewApplied: (state, action) => {
      state.defaultViewApplied = { ...state.defaultViewApplied, ...action.payload }
    },
  },
  extraReducers: builder => {
    builder.addCase(PURGE, () => initialState)
  },
})

// Actions
export const referralActions = {
  setReferralDetail: referralSlice.actions.setReferralDetail,
  setReferralMcmStatus: referralSlice.actions.setReferralMcmStatus,
  setReferralList: referralSlice.actions.setReferralList,
  setSelectedKeyword: referralSlice.actions.setSelectedKeyword,
  setAllKeyword: referralSlice.actions.setAllKeyword,
  setIsQuestionAnswersLoaded: referralSlice.actions.setIsQuestionAnswersLoaded,
  setCategoryDetailList: referralSlice.actions.setCategoryDetailList,
  setWaystarMatch: referralSlice.actions.setWaystarMatch,
  setPVerifyMatch: referralSlice.actions.setPVerifyMatch,
  setSelectedReferral: referralSlice.actions.setSelectedReferral,
  setSelectedParentReferral: referralSlice.actions.setSelectedParentReferral,
  setSelectedListTab: referralSlice.actions.setSelectedListTab,
  setReferralListRoot: referralSlice.actions.setReferralListRoot,
  setRouteList: referralSlice.actions.setRouteList,
  setRefRouteList: referralSlice.actions.setRefRouteList,
  setOrganizationList: referralSlice.actions.setOrganizationList,
  setMappedPverifyPayorsList: referralSlice.actions.setMappedPverifyPayorsList,
  setParentFilterItems: referralSlice.actions.setParentFilterItems,
  setChildFilterItems: referralSlice.actions.setChildFilterItems,
  setDefaultViewApplied: referralSlice.actions.setDefaultViewApplied,
  setGridStatusFilterItems: referralSlice.actions.setGridStatusFilterItems,
  setRefreshForSourceMessageData: referralSlice.actions.setRefreshForSourceMessageData,
  setSourceMessageTabToSet: referralSlice.actions.setSourceMessageTabToSet,
  setReferralListInputs: referralSlice.actions.setReferralListInputs,
  setIsTaskListPending: referralSlice.actions.setIsTaskListPending,
  setWaystarValidationChanged: referralSlice.actions.setWaystarValidationChanged,
  setHoldReasons: referralSlice.actions.setHoldReasons,
  setLostReasons: referralSlice.actions.setLostReasons,
  setDenialReasonList: referralSlice.actions.setDenialReasonList,
  setReferralGridViewList: referralSlice.actions.setReferralGridViewList,
  setFacilityListForParentFilter: referralSlice.actions.setFacilityListForParentFilter,
  setFacilityListForChildFilter: referralSlice.actions.setFacilityListForChildFilter,
  setInsuranceProvider: referralSlice.actions.setInsuranceProvider,
  setPVerifyLinks: referralSlice.actions.setPVerifyLinks,
  setPVerifyRequestId: referralSlice.actions.setPVerifyRequestId,
  setPVerifyValidationChanged: referralSlice.actions.setPVerifyValidationChanged,
  setSelectedParentTab: referralSlice.actions.setSelectedParentTab,
  setReferralComparisonId: referralSlice.actions.setReferralComparisonId,
  setFaxStatusRefresh: referralSlice.actions.setFaxStatusRefresh,
  setOcrCompletionStatus: referralSlice.actions.setOcrCompletionStatus,
  setAIProcessingStatus: referralSlice.actions.setAIProcessingStatus,
  setSourceHospitalList: referralSlice.actions.setSourceHospitalList,
  clearIndividualParentFilter: referralSlice.actions.clearIndividualParentFilter,
  clearIndividualChildFilter: referralSlice.actions.clearIndividualChildFilter,
  setRefreshRefList: referralSlice.actions.setRefreshRefList,
  setGridViewAccordionDetails: referralSlice.actions.setGridViewAccordionDetails,
  setShowDocument: referralSlice.actions.setShowDocument,
  setGridViewZoomedListId: referralSlice.actions.setGridViewZoomedListId,
  setSelectedPatientFacilityId: referralSlice.actions.setSelectedPatientfacilityId,
  setReloadPatientRefdata: referralSlice.actions.setReloadPatientRefdata,
  setChildRefdataForNav: referralSlice.actions.setChildRefdataForNav,
  setShowLock: referralSlice.actions.setShowLock,
  setSelectedAssignedUsers: referralSlice.actions.setSelectedAssignedUsers,
  setNoteTypeFullList: referralSlice.actions.setNoteTypeFullList,
  setSourcehospListForParentFilter: referralSlice.actions.setSourcehospListForParentFilter,
  setSourcehospListForChildFilter: referralSlice.actions.setSourcehospListForChildFilter,
  setLostRationaleListForParentFilter: referralSlice.actions.setLostRationaleListForParentFilter,
  setLostRationaleListForChildFilter: referralSlice.actions.setLostRationaleListForChildFilter,
  setHoldRationaleListForParentFilter: referralSlice.actions.setHoldRationaleListForParentFilter,
  setHoldRationaleListForChildFilter: referralSlice.actions.setHoldRationaleListForChildFilter,
  setCustomColumns: referralSlice.actions.setCustomColumns,
  setParentRefOfDuplicateRefs: referralSlice.actions.setParentRefOfDuplicateRefs,
  setIsParentPathOrigin: referralSlice.actions.setIsParentPathOrigin,
  setIsQueuedPathOrigin: referralSlice.actions.setIsQueuedPathOrigin,
  setQueuedchildRefId: referralSlice.actions.setQueuedchildRefId,
  setQueuedFileId: referralSlice.actions.setQueuedFileId,
  setIsDialogOpen: referralSlice.actions.setIsDialogOpen,
  setIsNotesDrawerOpen: referralSlice.actions.setIsNotesDrawerOpen,
  setNoteIdToEdit: referralSlice.actions.setNoteIdToEdit,
  setIsSideDrawerOpen: referralSlice.actions.setIsSideDrawerOpen,
  setReferralAttachment: referralSlice.actions.updateReferralAttachment,
  setCapabilityViewMode: referralSlice.actions.setCapabilityViewMode,
  setCapabilityExpandedAccordion: referralSlice.actions.setCapabilityExpandedAccordion,
  setClinicalReviewAccordionPreference: referralSlice.actions.setClinicalReviewAccordionPreference,
}

// Selectors
export const selectReferralDetail = (state: RootState) => state.referral.referralDetail
export const selectReferralList = (state: RootState) => state.referral.referralList
export const getSelectedKeyword = (state: RootState) => state.referral.selectedKeyword
export const getAllKeyword = (state: RootState) => state.referral.allKeyword
export const getIsQuestionAnswersLoaded = (state: RootState) =>
  state.referral.isQuestionAnswersLoaded
export const getCategoryDetailList = (state: RootState) => state.referral.categoryDetailList

export const getSelectedReferral = (state: RootState) => state.referral.selectedReferral
export const selectedParentReferral = (state: RootState) => state.referral.selectedParentReferral
export const getSelectedListTab = (state: RootState) => state.referral.selectedListTab
export const getReferralListRoot = (state: RootState) => state.referral.referralListRoot
export const getRouteList = (state: RootState) => state.referral.routeList
export const getRefRouteList = (state: RootState) => state.referral.refRouteList
export const getSourceHospitalList = (state: RootState) => state.referral.sourceHospitalList
export const getOrganizationList = (state: RootState) => state.referral.organizationList
export const getMappedPverifyPayorsFullList = (state: RootState) =>
  state.referral.mappedPverifypayorsFullList
export const getParentFilterItems = (state: RootState) => state.referral.parentFilterItems
export const getChildFilterItems = (state: RootState) => state.referral.childFilterItems
export const getDefaultViewApplied = (state: RootState) => state.referral.defaultViewApplied
export const getRefreshForSourceMessageData = (state: RootState) =>
  state.referral.refreshForSourceMessageData
export const getSourceMessageTabToSet = (state: RootState) => state.referral.sourceMessageTabToSet

export const getReferralListInputs = (state: RootState) => state.referral.referralListInputs
export const getIsTaskListPending = (state: RootState) => state.referral.isTaskListPending
export const waystarValidationChanged = (state: RootState) =>
  state.referral.waystarValidationChanged
export const getHoldReasons = (state: RootState) => state.referral.holdReasons
export const getLostReasons = (state: RootState) => state.referral.lostReasons
export const getDenialReasonList = (state: RootState) => state.referral.denialReasonList
export const getReferralGridViewList = (state: RootState) => state.referral.referralGridViewList
export const getFacilityListForParentFilter = (state: RootState) =>
  state.referral.facilityListForParentFilter
export const getFacilityListForChildFilter = (state: RootState) =>
  state.referral.facilityListForChildFilter
export const getInsuranceProvider = (state: RootState) => state.referral.insuranceProvider
export const getPVerifyLinks = (state: RootState) => state.referral.pVerifyLinks
export const getPVerifyRequestId = (state: RootState) => state.referral.pVerifyRequestId
export const pVerifyValidationChanged = (state: RootState) =>
  state.referral.pVerifyValidationChanged
export const getSelectedParentTab = (state: RootState) => state.referral.selectedParentTab
export const getFaxStatusRefresh = (state: RootState) => state.referral.faxStatusRefresh
export const getRefreshForRefList = (state: RootState) => state.referral.refreshRefList
export const getGridViewAccordionDetails = (state: RootState) =>
  state.referral.gridViewAccordionDetails
export const getShowDocumentBool = (state: RootState) => state.referral.showDocument
export const getGridViewZoomedListId = (state: RootState) => state.referral.setGridViewZoomedListId
export const getSelectedPatientFacilityId = (state: RootState) =>
  state.referral.selectedPatientFacId
export const getReloadPatientRefdata = (state: RootState) => state.referral.reloadPatientRefdata
export const getChildRefdataForNav = (state: RootState) => state.referral.childRefdataForNav
export const getShowLock = (state: RootState) => state.referral.showLock
export const getSelectedAssignedUsers = (state: RootState) => state.referral.selectedAssignedUsers
export const getNoteTypeFullList = (state: RootState) => state.referral.noteTypeFullList

export const getSourcehospListForParentFilter = (state: RootState) =>
  state.referral.sourcehospListForParentFilter
export const getSourcehospListForChildFilter = (state: RootState) =>
  state.referral.sourcehospListForChildFilter
export const getLostRationaleListForParentFilter = (state: RootState) =>
  state.referral.lostRationaleListForParentFilter
export const getLostRationaleListForChildFilter = (state: RootState) =>
  state.referral.lostRationaleListForChildFilter
export const getHoldRationaleListForParentFilter = (state: RootState) =>
  state.referral.holdRationaleListForParentFilter
export const getHoldRationaleListForChildFilter = (state: RootState) =>
  state.referral.holdRationaleListForChildFilter
export const getCustomColumn = (state: RootState) => state.referral.customColumns
export const getParentRefOfDuplicateRefs = (state: RootState) =>
  state.referral.parentRefOfDuplicateRefs
export const isParentPathOrigin = (state: RootState) => state.referral.isParentPathOrigin
export const isQueuedPathOrigin = (state: RootState) => state.referral.isQueuedPathOrigin
export const getQueuedchildRefId = (state: RootState) => state.referral.queuedChildRefId
export const getQueuedFileId = (state: RootState) => state.referral.queuedFileId
export const isDialogOpen = (state: RootState) => state.referral.isDialogOpen
export const isNotesDrawerOpen = (state: RootState) => state.referral.isNotesDrawerOpen
export const getNoteIdToEdit = (state: RootState) => state.referral.noteIdToEdit
export const isSideDrawerOpen = (state: RootState) => state.referral.isSideDrawerOpen
export const getCapabilityViewMode = (state: RootState) => state.referral.capabilityViewMode
export const getCapabilityExpandedAccordion = (state: RootState) =>
  state.referral.capabilityExpandedAccordion
export const getClinicalReviewAccordionPreference = (state: RootState) =>
  state.referral.clinicalReviewAccordionPreference
// Reducer
export default referralSlice.reducer
